import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { isGameMaster, formatRelativeTime, getAvatarColor, getInitials } from '../lib/utils';
import { computeCompleteness } from '../components/character/CharacterSheetRenderer';
import {
  Loader2,
  Users,
  Search,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Download,
  History,
  Plus,
  ClipboardList,
  Shield,
  Check,
  AlertCircle,
  ArrowUpDown,
  Pencil,
} from 'lucide-react';

const API_URL = '/api';

// ── Indicateur de complétude ──────────────────────────────────────────────
function CompletenessBar({ pct }) {
  if (pct === null) return <span className="text-xs text-muted-foreground">—</span>;
  const color =
    pct >= 80 ? 'bg-green-500' :
    pct >= 40 ? 'bg-amber-500' :
                'bg-red-500';
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 rounded-full bg-border overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground tabular-nums w-8">{pct}%</span>
    </div>
  );
}

// ── Quick HP edit ─────────────────────────────────────────────────────────
function QuickHpEdit({ sheet, hpKey, hpMaxKey, onSaved, language }) {
  const [editing, setEditing]   = useState(false);
  const [value,   setValue]     = useState('');
  const [saving,  setSaving]    = useState(false);
  const inputRef                = useRef(null);

  const current = sheet?.data?.[hpKey];
  const max     = sheet?.data?.[hpMaxKey];
  const display = max != null ? `${current ?? '?'}/${max}` : (current ?? '—');

  const startEdit = () => {
    setValue(String(current ?? ''));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 50);
  };

  const commit = async () => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed === current) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API_URL}/campaigns/${sheet.campaign_id}/character-sheets/${sheet.id}`,
        { data: { ...sheet.data, [hpKey]: parsed }, version: sheet.version }
      );
      onSaved(res.data);
      toast.success(language === 'fr' ? 'PV mis à jour' : 'HP updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          ref={inputRef}
          type="number"
          value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          className="h-6 w-16 text-xs px-1"
        />
        {max != null && <span className="text-xs text-muted-foreground">/{max}</span>}
        {saving && <Loader2 className="h-3 w-3 animate-spin" />}
      </div>
    );
  }

  return (
    <button
      onClick={startEdit}
      className="text-xs font-mono hover:text-primary flex items-center gap-1 group"
      title={language === 'fr' ? 'Modifier les PV' : 'Edit HP'}
    >
      {display}
      <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-60" />
    </button>
  );
}

// ── Page principale ────────────────────────────────────────────────────────
export default function GMCharactersPage() {
  const { campaignId } = useParams();
  const navigate       = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();

  const [loading,    setLoading]    = useState(true);
  const [creating,   setCreating]   = useState(null); // userId en cours de création
  const [sheets,     setSheets]     = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [search,     setSearch]     = useState('');
  const [sortKey,    setSortKey]    = useState('player');   // 'player' | 'character' | 'updated_at'
  const [sortDir,    setSortDir]    = useState('asc');      // 'asc' | 'desc'

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ     = isGameMaster(userRole);

  // ── Chargement ────────────────────────────────────────────────────────
  // NOTE: tous les hooks doivent être déclarés avant tout return conditionnel
  const loadData = useCallback(async () => {
    if (!campaignId) return;
    if (!currentCampaign) return; // Wait for campaign to load from context
    if (!currentCampaign.system_id) {
      setSheets([]);
      setTemplates([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [sheetsRes, tplsRes] = await Promise.all([
        axios.get(`${API_URL}/campaigns/${campaignId}/character-sheets`),
        axios.get(`${API_URL}/game-systems/${currentCampaign.system_id}/templates`),
      ]);
      setSheets(sheetsRes.data);
      setTemplates(tplsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setLoading(false);
    }
  }, [campaignId, currentCampaign, language]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Redirection si pas MJ (après tous les hooks) ──────────────────────
  if (!loading && currentCampaign && !isMJ) {
    return <Navigate to={`/campaigns/${campaignId}/character-sheet`} replace />;
  }

  // ── Créer fiche pour un joueur (MJ) ──────────────────────────────────
  const handleCreateSheet = async (targetUserId) => {
    const defaultTpl = templates.find(tp => tp.is_default) || templates[0];
    if (!defaultTpl) {
      toast.error(language === 'fr' ? 'Aucun template disponible' : 'No template available');
      return;
    }
    setCreating(targetUserId);
    try {
      const res = await axios.post(
        `${API_URL}/campaigns/${campaignId}/character-sheets`,
        { template_id: defaultTpl.id, data: {}, target_user_id: targetUserId }
      );
      setSheets(prev => [...prev.filter(s => s.user_id !== targetUserId), res.data]);
      toast.success(language === 'fr' ? 'Fiche créée' : 'Sheet created');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setCreating(null);
    }
  };

  // ── Export PDF ────────────────────────────────────────────────────────
  const handleExportPdf = async (sheet) => {
    try {
      const res = await axios.get(
        `${API_URL}/campaigns/${campaignId}/character-sheets/${sheet.id}/export/pdf`,
        { responseType: 'blob' }
      );
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a    = document.createElement('a');
      const disp = res.headers['content-disposition'] || '';
      const m    = disp.match(/filename="?([^";\n]+)"?/i);
      a.href     = url;
      a.download = m?.[1] || `sheet_${sheet.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (err) {
      toast.error(
        err.response?.status === 422
          ? (language === 'fr' ? 'Pas d\'overlay PDF configuré' : 'No PDF overlay configured')
          : t('error_generic', language)
      );
    }
  };

  // ── Mise à jour locale après quick edit ──────────────────────────────
  const handleSheetUpdated = (updatedSheet) => {
    setSheets(prev => prev.map(s => s.id === updatedSheet.id ? updatedSheet : s));
  };

  // ── Construction de la table ──────────────────────────────────────────
  const members = currentCampaign?.members || [];

  // Détecter les clés intelligentes (hp, level, character_name) dans les templates
  const templateMap = {};
  for (const tpl of templates) {
    templateMap[tpl.id] = tpl;
  }
  const detectKey = (schema, ...candidates) => {
    if (!schema?.properties) return null;
    return candidates.find(k => k in schema.properties) || null;
  };

  const rows = members
    .filter(m => m.role === 'member') // Seulement les joueurs (pas les co-MJ)
    .map(member => {
      const sheet = sheets.find(s => s.user_id === member.user_id) || null;
      const tpl   = sheet ? templateMap[sheet.template_id] : null;
      const fs    = tpl?.fields_schema;

      const characterName = sheet?.data?.character_name
        || sheet?.data?.name
        || sheet?.data?.character?.name
        || null;

      const hpKey    = detectKey(fs, 'hp', 'hit_points', 'pv');
      const hpMaxKey = detectKey(fs, 'hp_max', 'max_hp', 'pv_max');
      const levelKey = detectKey(fs, 'level', 'niveau');

      const hpVal    = hpKey    ? (typeof sheet?.data?.[hpKey] === 'object'
                        ? sheet?.data?.[hpKey]?.current : sheet?.data?.[hpKey]) : null;
      const hpMaxVal = hpMaxKey ? sheet?.data?.[hpMaxKey] : null;
      const levelVal = levelKey ? sheet?.data?.[levelKey] : null;

      const completeness = tpl && sheet
        ? computeCompleteness(fs, sheet.data)
        : null;

      return { member, sheet, tpl, characterName, hpKey, hpMaxKey, hpVal, hpMaxVal, levelVal, completeness };
    });

  // ── Filtrage ──────────────────────────────────────────────────────────
  const q = search.toLowerCase().trim();
  const filtered = rows.filter(row => {
    if (!q) return true;
    const playerName = (row.member.character_name || row.member.username || '').toLowerCase();
    const charName   = (row.characterName || '').toLowerCase();
    return playerName.includes(q) || charName.includes(q);
  });

  // ── Tri ───────────────────────────────────────────────────────────────
  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    if (sortKey === 'player') {
      va = (a.member.character_name || a.member.username || '').toLowerCase();
      vb = (b.member.character_name || b.member.username || '').toLowerCase();
    } else if (sortKey === 'character') {
      va = (a.characterName || '').toLowerCase();
      vb = (b.characterName || '').toLowerCase();
    } else {
      va = a.sheet?.updated_at || '';
      vb = b.sheet?.updated_at || '';
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />;
    return sortDir === 'asc'
      ? <ChevronUp   className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // ── Affichage chargement ──────────────────────────────────────────────
  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  // ── Aucun système de jeu configuré ──────────────────────────────────
  if (!currentCampaign?.system_id) {
    return (
      <MainLayout>
        <div className="space-y-5 animate-fade-in">
          <h1 className="font-cinzel text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            {language === 'fr' ? 'Fiches des joueurs' : 'Player Sheets'}
          </h1>
          <Card className="glass-card">
            <CardContent className="py-12 text-center space-y-3">
              <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground/40" />
              <p className="text-muted-foreground">
                {language === 'fr'
                  ? 'Aucun système de jeu n\'est associé à cette campagne.'
                  : 'No game system is associated with this campaign.'}
              </p>
              <p className="text-sm text-muted-foreground">
                {language === 'fr'
                  ? 'Configurez un système de jeu dans les paramètres pour activer les fiches de personnage.'
                  : 'Set up a game system in settings to enable character sheets.'}
              </p>
              <Button
                variant="outline"
                onClick={() => navigate(`/campaigns/${campaignId}/settings`)}
              >
                {language === 'fr' ? 'Aller aux paramètres' : 'Go to settings'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // ── Aucun joueur dans la campagne ───────────────────────────────────
  if (rows.length === 0) {
    return (
      <MainLayout>
        <div className="space-y-5 animate-fade-in">
          <h1 className="font-cinzel text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            {language === 'fr' ? 'Fiches des joueurs' : 'Player Sheets'}
          </h1>
          <Card className="glass-card">
            <CardContent className="py-12 text-center space-y-3">
              <Users className="h-10 w-10 mx-auto text-muted-foreground/40" />
              <p className="text-muted-foreground">
                {language === 'fr'
                  ? 'Aucun joueur n\'a encore rejoint cette campagne.'
                  : 'No players have joined this campaign yet.'}
              </p>
              <p className="text-sm text-muted-foreground">
                {language === 'fr'
                  ? 'Invitez des joueurs depuis le dashboard de la campagne.'
                  : 'Invite players from the campaign dashboard.'}
              </p>
              <Button
                variant="outline"
                onClick={() => navigate(`/campaigns/${campaignId}`)}
              >
                {language === 'fr' ? 'Retour au dashboard' : 'Back to dashboard'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-5 animate-fade-in">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-cinzel text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6" />
              {language === 'fr' ? 'Fiches des joueurs' : 'Player Sheets'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'fr'
                ? `${sorted.length} joueur(s) · ${sheets.length} fiche(s)`
                : `${sorted.length} player(s) · ${sheets.length} sheet(s)`}
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={language === 'fr' ? 'Rechercher joueur / personnage…' : 'Search player / character…'}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>
        </div>

        {/* ── Table ──────────────────────────────────────────────────────── */}
        <Card className="glass-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left p-3 font-semibold text-muted-foreground">
                    <button onClick={() => toggleSort('player')} className="flex items-center gap-1 hover:text-foreground">
                      {language === 'fr' ? 'Joueur' : 'Player'} <SortIcon col="player" />
                    </button>
                  </th>
                  <th className="text-left p-3 font-semibold text-muted-foreground">
                    <button onClick={() => toggleSort('character')} className="flex items-center gap-1 hover:text-foreground">
                      {language === 'fr' ? 'Personnage' : 'Character'} <SortIcon col="character" />
                    </button>
                  </th>
                  <th className="text-left p-3 font-semibold text-muted-foreground hidden sm:table-cell">
                    {language === 'fr' ? 'Niv.' : 'Lvl'}
                  </th>
                  <th className="text-left p-3 font-semibold text-muted-foreground hidden md:table-cell">
                    PV / HP
                  </th>
                  <th className="text-left p-3 font-semibold text-muted-foreground hidden lg:table-cell">
                    {language === 'fr' ? 'Complétude' : 'Completion'}
                  </th>
                  <th className="text-left p-3 font-semibold text-muted-foreground">
                    {language === 'fr' ? 'Statut' : 'Status'}
                  </th>
                  <th className="text-left p-3 font-semibold text-muted-foreground">
                    <button onClick={() => toggleSort('updated_at')} className="flex items-center gap-1 hover:text-foreground">
                      {language === 'fr' ? 'Modif.' : 'Modified'} <SortIcon col="updated_at" />
                    </button>
                  </th>
                  <th className="text-right p-3 font-semibold text-muted-foreground">
                    {language === 'fr' ? 'Actions' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-muted-foreground">
                      <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      {language === 'fr' ? 'Aucun joueur trouvé' : 'No players found'}
                    </td>
                  </tr>
                )}
                {sorted.map(({ member, sheet, tpl, characterName, hpKey, hpMaxKey, levelVal, completeness }) => {
                  const playerName = member.character_name || member.username || member.user_id.slice(0, 8);
                  const initials   = getInitials(playerName);
                  const avatarBg   = getAvatarColor(member.user_id);
                  const hasPdf     = !!(tpl?.pdf_asset_id && tpl?.pdf_overlay?.pages?.length > 0);

                  return (
                    <tr key={member.user_id} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                      {/* Joueur */}
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${avatarBg}`}>
                            {initials}
                          </div>
                          <span className="font-medium truncate max-w-[120px]">{playerName}</span>
                        </div>
                      </td>

                      {/* Personnage */}
                      <td className="p-3">
                        <span className="text-muted-foreground truncate max-w-[120px] block">
                          {characterName || '—'}
                        </span>
                      </td>

                      {/* Niveau */}
                      <td className="p-3 hidden sm:table-cell">
                        <span className="text-muted-foreground tabular-nums">{levelVal ?? '—'}</span>
                      </td>

                      {/* HP quick edit */}
                      <td className="p-3 hidden md:table-cell">
                        {sheet && hpKey ? (
                          <QuickHpEdit
                            sheet={sheet}
                            hpKey={hpKey}
                            hpMaxKey={hpMaxKey}
                            onSaved={handleSheetUpdated}
                            language={language}
                          />
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>

                      {/* Complétude */}
                      <td className="p-3 hidden lg:table-cell">
                        <CompletenessBar pct={completeness} />
                      </td>

                      {/* Statut */}
                      <td className="p-3">
                        {sheet ? (
                          <Badge variant="outline" className="text-xs gap-1 text-green-600 border-green-600/40">
                            <Check className="h-3 w-3" />
                            {language === 'fr' ? 'Créée' : 'Created'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs gap-1 text-amber-600 border-amber-600/40">
                            <AlertCircle className="h-3 w-3" />
                            {language === 'fr' ? 'Manquante' : 'Missing'}
                          </Badge>
                        )}
                      </td>

                      {/* Dernière modif */}
                      <td className="p-3">
                        <span className="text-xs text-muted-foreground">
                          {sheet ? formatRelativeTime(sheet.updated_at, language === 'fr' ? 'fr-FR' : 'en-US') : '—'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          {sheet ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                onClick={() => navigate(`/campaigns/${campaignId}/characters/${sheet.id}`)}
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                {language === 'fr' ? 'Ouvrir' : 'Open'}
                              </Button>
                              {hasPdf && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => handleExportPdf(sheet)}
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs"
                                onClick={() => navigate(`/campaigns/${campaignId}/characters/${sheet.id}?tab=history`)}
                              >
                                <History className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() => handleCreateSheet(member.user_id)}
                              disabled={creating === member.user_id}
                            >
                              {creating === member.user_id
                                ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                : <Plus className="h-3 w-3 mr-1" />}
                              {language === 'fr' ? 'Créer' : 'Create'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ── Section Fiches co-MJ ────────────────────────────────────────── */}
        {(() => {
          const gmMembers = members.filter(m => m.role !== 'member' && m.user_id !== user?.id);
          if (gmMembers.length === 0) return null;
          const gmSheets = gmMembers.flatMap(m => sheets.filter(s => s.user_id === m.user_id));
          if (gmSheets.length === 0) return null;
          return (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  {language === 'fr' ? 'Fiches co-MJ' : 'Co-GM Sheets'}
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {gmSheets.map(sheet => {
                  const m = gmMembers.find(gm => gm.user_id === sheet.user_id);
                  return (
                    <Button
                      key={sheet.id}
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/campaigns/${campaignId}/characters/${sheet.id}`)}
                    >
                      <ClipboardList className="h-3.5 w-3.5 mr-1" />
                      {m?.character_name || m?.username || sheet.user_id.slice(0, 8)}
                    </Button>
                  );
                })}
              </div>
            </div>
          );
        })()}

      </div>
    </MainLayout>
  );
}
